import React, { useMemo } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { Separator } from '@fluentui/react';

import { runCancelableAction } from 'src/services/utils';
import ListNav from 'src/components/base/ListNav';
import { officeStore } from 'src/stores';

import {
  FlowstepCategory, Type, RangeType,
  InsertFlowsteps, DeleteFlowsteps, AdminToolFlowsteps,
  getRoute, isPushKind, toDisplayName
} from 'shared/utils/flowstep';
import { isEmpty } from 'shared/utils/flow';
import { capitalizeString } from 'shared/utils/utils';


const Const = {
  Run: { All: 'all', Worksheet: 'worksheet' },
  separatorStyles: { root: { padding: 0, height: 10 } }
};


function typeToMenuItem(type, range = '', onClickHandler = () => { }) {
  return {
    key: `${type} ${range}`,
    text: range
      ? capitalizeString(`${type} ${range}`.toLowerCase())
      : toDisplayName(type),
    onClick: () => onClickHandler(type, range),
  };
}

function typesToMenuItems(types, addSeparator = true, onClickHandler = null) {
  return types.reduce((out, type, index) => {
    if (isPushKind(type)) {
      out.push(typeToMenuItem(type, RangeType.All, onClickHandler));
      out.push(typeToMenuItem(type, RangeType.Selected, onClickHandler));
    }
    else {
      out.push(typeToMenuItem(type, '', onClickHandler));
    }

    if (addSeparator && index !== types.length - 1) {
      out.push(null); // Separator
    }
    return out;
  }, []);
}


function createHomeBlocks({ license, handlers }) {
  const { flows, adminTools, flowsteps } = license;
  const { push, pull } = flowsteps;
  const { handleRunPull, handleRunAllFlows, handleConvertIds, onMenuItemClick } = handlers;

  const flowMenuItems = officeStore?.flows.reduce((out, flow) => {
    !isEmpty(flow) && out.push({
      text: flow.name,
      onClick: () => handlers.executeFlows ? handlers.executeFlows(flow) : () => { }
    });
    return out;
  }, []);

  const pushButtonsLicensing = push.createOrRemove.limited;
  const seeFlowTabContentLicensing = flows.seeTabContent;

  const pullItems = [
    {
      title: 'Import Data',
      secondaryText: 'Import data from a Salesforce report, query, search, or CSV file',
      teaching: { id: 'home-pull-data' },
      menuItems: typesToMenuItems([
        pull.createOrRemove.report.can && Type.Report,
        pull.createOrRemove.soql.can && Type.Soql,
        pull.createOrRemove.csv.can && Type.Csv,
        pull.createOrRemove.sosl.can && Type.Sosl,
      ].filter(Boolean), false, onMenuItemClick)
    },
    {
      title: 'Refresh Data',
      secondaryText: 'Refresh a previously connected Salesforce report, SOQL query or CSV file',
      teaching: { id: 'home-refresh-data' },
      menuItems: pull.seeRefreshButton.can ?
        [
          {
            text: 'Active worksheet',
            onClick: () => handleRunPull(Const.Run.Worksheet)
          },
          {
            text: 'All worksheets',
            licensing: pull.runAll,
            onClick: () => handleRunPull(Const.Run.All),
          }
        ]
        :
        [],
    },
  ].filter(x => x.menuItems.length);

  const pushItems = push.seeHomeButtons.can
    ? [{
      title: 'Create/Modify',
      secondaryText: `Create or modify records in Salesforce using data from Excel rows`,
      teaching: { id: 'home-create' },
      licensing: pushButtonsLicensing,
      menuItems: typesToMenuItems((InsertFlowsteps.filter(step => push.massPush[step].can)), true, onMenuItemClick),
    },
    {
      title: 'Delete/Restore',
      secondaryText: `Delete or Undelete records in Salesforce using IDs in Excel rows`,
      teaching: { id: 'home-delete' },
      licensing: pushButtonsLicensing,
      menuItems: typesToMenuItems(push.massPush.deleteOrRestore.can ? DeleteFlowsteps : [], true, onMenuItemClick),
    },
    ]
    : [];

  const flowItems = [
    flows.run.can && {
      title: 'Run Flow',
      secondaryText: `Execute a flow right away`,
      teaching: { id: 'home-run-flow' },
      licensing: seeFlowTabContentLicensing,
      menuItems: flowMenuItems
    },
    flows.seeRunAllAtHome.can && {
      title: 'Run All Flows',
      secondaryText: `Execute all flows in the entire workbook`,
      teaching: { id: 'home-run-all-flows' },
      licensing: seeFlowTabContentLicensing,
      onClick: handleRunAllFlows
    },
  ];

  const adminItems = adminTools.seeHomeButton.can
    ? [{
      title: 'Admin Tools',
      secondaryText: `Various admin tools`,
      teaching: { id: 'home-admin-tools' },
      licensing: license.adminTools.use,
      menuItems: [
        { text: 'Convert IDs', onClick: handleConvertIds },
        typeToMenuItem(Type.ConvertLeads, '', onMenuItemClick),
        typeToMenuItem(Type.MergeObjects, '', onMenuItemClick),
      ]
    },
      null, // Placeholder
    ]
    : [];

  return [pullItems, pushItems, flowItems, adminItems].reduce((out, x) => {
    const filtered = x.filter(item => item && (!item.menuItems || item.menuItems.length));
    filtered.length && out.push(filtered);
    return out;
  }, []);
}


export function hasHomeButtons(license) {
  const mockHandlers = {
    handleRunPull: () => { },
    handleRunAllFlows: () => { },
    handleConvertIds: () => { },
    executeFlows: () => { },
    onMenuItemClick: null
  };

  const blocks = createHomeBlocks({ license, handlers: mockHandlers });
  return blocks.length > 0;
}

const ActionList = inject('officeStore', 'userStore')(observer(({ officeStore, userStore }) => {
  const history = useHistory();

  function handleMenuItemClick(type, range) {
    const route = getRoute({ type, range });;
    history.push(route);
  }

  async function executeFlows(flows) {
    await runCancelableAction(async (token) => {
      await officeStore.executeFlow(flows, token);
    })
  }

  async function handleRunPull(run) {
    await runCancelableAction(async (token) => {
      await (run === Const.Run.All
        ? officeStore.refreshAllSheets(FlowstepCategory.Pull, token)
        : officeStore.refreshCurrentSheet(FlowstepCategory.Pull, token));
    });
  }

  async function handleRunAllFlows() {
    await executeFlows([...officeStore.flows]);
  }

  async function handleConvertIds() {
    await runCancelableAction(async (token) => {
      await officeStore.convertIds(token);
    })
  }

  const blocks = createHomeBlocks({
    license: userStore.license,
    handlers: {
      handleRunPull,
      handleRunAllFlows,
      handleConvertIds,
      executeFlows,
      onMenuItemClick: handleMenuItemClick
    }
  });

  return (
    <div>
      {blocks.map((items, index) =>
        <ListNavBlock
          key={index}
          items={items}
          isFirst={index === 0}
        />
      )}
    </div>
  )
}))


function ListNavBlock({ items, isFirst = false }) {
  if (items.length !== 2) {
    items.push(null); // placeholder
  }

  return (
    <>
      {!isFirst && <Separator styles={Const.separatorStyles} />}
      <ListNav items={items} />
    </>
  );
}

export default observer(({ match }) => (
  <Switch>
    <Route path={`${match.url}`} component={ActionList} />
  </Switch>
));
